import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useState,
} from 'react';
import { ENV_VARIABLES } from 'src/common/constants';
import {
  getPlaylistUrl,
  testVideoSource,
} from 'src/components/utils/video-streaming-utils';

export type VideoStreamContextType = {
  localServerUrl: string;
  probeVideoSource: (
    moduleId: string,
  ) => Promise<{ playlistUrl: string; type: STREAM_SOURCE_TYPE } | undefined>;
  remoteServerUrl: string;
  setStreamSource: React.Dispatch<
    React.SetStateAction<Record<string, STREAM_SOURCE_TYPE>>
  >;
  streamSources: Record<string, STREAM_SOURCE_TYPE>;
  viewerToken: string;
};
export const VideoStreamContext = createContext<VideoStreamContextType>(
  {} as VideoStreamContextType,
);
export type STREAM_SOURCE_TYPE = 'local' | 'remote';

export const VideoStreamProvider = (props: PropsWithChildren) => {
  const {
    LOCAL_STREAMING_SERVER_URL: localServerUrl,
    STREAMING_SERVER_URL: remoteServerUrl,
    STREAMING_SERVER_VIEWER_TOKEN: viewerToken,
  } = ENV_VARIABLES;
  const [streamSource, setStreamSource] = useState<
    Record<string, STREAM_SOURCE_TYPE>
  >({});
  const probeVideoSource = useCallback(
    async (moduleId: string) => {
      // Local stream
      const localServerPlaylistUrl = getPlaylistUrl(localServerUrl, moduleId);
      const isLocalServerAvailable = await testVideoSource(
        localServerPlaylistUrl,
      );
      if (isLocalServerAvailable) {
        console.log(`Using local server for module ${moduleId}`);
        setStreamSource((prev) => ({
          ...prev,
          [moduleId]: 'local',
        }));
        return {
          playlistUrl: localServerPlaylistUrl,
          type: 'local',
        } as const;
      }
      const remoteServerPlaylistUrl = getPlaylistUrl(
        remoteServerUrl,
        moduleId,
        viewerToken,
        false,
      );
      const isRemoteServerAvailable = await testVideoSource(
        remoteServerPlaylistUrl,
      );
      if (isRemoteServerAvailable) {
        console.log(`Using remote server for module ${moduleId}`);
        setStreamSource((prev) => ({
          ...prev,
          [moduleId]: 'remote',
        }));
        return {
          playlistUrl: remoteServerPlaylistUrl,
          type: 'remote',
        } as const;
      }
      setStreamSource((prev) => {
        if (!(moduleId in prev)) return prev;
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { [moduleId]: _, ...newSources } = prev;
        return newSources;
      });
    },
    [localServerUrl, remoteServerUrl, viewerToken],
  );
  return (
    <VideoStreamContext.Provider
      value={{
        localServerUrl,
        probeVideoSource,
        remoteServerUrl,
        setStreamSource,
        streamSources: streamSource,
        viewerToken,
      }}
    >
      {props.children}
    </VideoStreamContext.Provider>
  );
};

export const useVideo = () => {
  const context = useContext(VideoStreamContext);
  if (!context) {
    throw new Error('useVideo must be used within a VideoProvider');
  }
  return context;
};
